/**
 * beforeinstallprompt 이벤트 캡처 + 설치 배너 노출 정책.
 *
 * 노출 조건 (모두 만족):
 *  1. 모바일 (viewport ≤ 768px 또는 UA 모바일)
 *  2. 미설치 (display-mode standalone 아님)
 *  3. 재방문 ≥ 2회
 *  4. 마지막 dismiss로부터 7일 경과
 *  5. beforeinstallprompt 이벤트 캡처 완료
 *
 * 영구지침: docs/develop/pwa/pwa-spec.md §3.5
 */

import { safeLocalStorage } from '@/lib/safeStorage'

const STORAGE_VISIT_COUNT = 'cb:visitCount'
const STORAGE_BANNER_DISMISSED_AT = 'cb:installBannerDismissedAt'
const DISMISS_COOLDOWN_DAYS = 7
const MIN_VISIT_COUNT = 2

export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
  prompt(): Promise<void>
}

let deferredPrompt: BeforeInstallPromptEvent | null = null

/** beforeinstallprompt 캡처 + 방문 횟수 카운트 시작 */
export function initInstallPromptCapture(): void {
  if (typeof window === 'undefined') return

  // 방문 횟수 +1 (per session 아님, per page load)
  incrementVisitCount()

  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    deferredPrompt = e as BeforeInstallPromptEvent
    window.dispatchEvent(new CustomEvent('pwa:install-available'))
  })

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null
    window.dispatchEvent(new CustomEvent('pwa:installed'))
  })
}

function incrementVisitCount(): void {
  const current = Number(safeLocalStorage.getItem(STORAGE_VISIT_COUNT) ?? '0')
  safeLocalStorage.setItem(STORAGE_VISIT_COUNT, String(current + 1))
}

export function getVisitCount(): number {
  return Number(safeLocalStorage.getItem(STORAGE_VISIT_COUNT) ?? '0')
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  )
}

export function isMobileViewport(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(max-width: 768px)').matches
}

export function isInstallPromptAvailable(): boolean {
  return deferredPrompt !== null
}

export function getDeferredPrompt(): BeforeInstallPromptEvent | null {
  return deferredPrompt
}

export function clearDeferredPrompt(): void {
  deferredPrompt = null
}

/** 마지막 dismiss로부터 7일 경과 여부 */
export function isDismissedRecently(): boolean {
  const dismissedAt = safeLocalStorage.getItem(STORAGE_BANNER_DISMISSED_AT)
  if (!dismissedAt) return false
  const elapsed = Date.now() - Number(dismissedAt)
  return elapsed < DISMISS_COOLDOWN_DAYS * 24 * 60 * 60 * 1000
}

export function markBannerDismissed(): void {
  safeLocalStorage.setItem(STORAGE_BANNER_DISMISSED_AT, String(Date.now()))
}

/** 배너 노출 조건 종합 판정 */
export function shouldShowInstallBanner(): boolean {
  return (
    isMobileViewport() &&
    !isStandalone() &&
    getVisitCount() >= MIN_VISIT_COUNT &&
    !isDismissedRecently() &&
    isInstallPromptAvailable()
  )
}
